import React from 'react';

import { isNil } from 'es-toolkit';

import { Nullable } from '@clearverify/types';

import {
  PeoRevenueShareDocument,
  PeoUpdate,
  usePeoRevenueShareQuery,
  usePeoRevenueShareUpdateMutation,
  useRevenueShareSettingDeleteMutationMutation,
} from 'src/api';

import { useNotification } from '../app';

export const usePeoRevenueShare = (id: Nullable<string>) => {
  const { createApolloContext } = useNotification();

  const { data, loading } = usePeoRevenueShareQuery({
    variables: {
      where: { id },
    },
    skip: !id,
  });

  const peoBilling = React.useMemo(() => data?.peo, [data?.peo]);

  const [peoRevenueShareUpdateMutation, { loading: updating }] = usePeoRevenueShareUpdateMutation({
    refetchQueries: [PeoRevenueShareDocument],
    context: createApolloContext({
      successMessage: 'Peo revenue share has been successfully updated',
    }),
  });

  const [peoBillingDeleteMutation, { loading: deleting }] =
    useRevenueShareSettingDeleteMutationMutation({
      refetchQueries: [PeoRevenueShareDocument],
      context: createApolloContext({
        successMessage: 'Peo revenue share has been successfully updated',
      }),
    });

  const updatePeoRevenueShare = async (data: Omit<PeoUpdate, 'id'>) => {
    if (isNil(id)) {
      return;
    }

    await peoRevenueShareUpdateMutation({
      variables: {
        where: { id },
        data,
      },
    });
  };

  const deletePeoBilling = async (data: { id: Nullable<string> }) => {
    if (isNil(data.id)) {
      return;
    }

    await peoBillingDeleteMutation({
      variables: {
        filter: {
          id: data.id,
        },
      },
    });
  };

  return {
    peoBilling,
    updatePeoRevenueShare,
    deletePeoBilling,
    loading: loading || updating || deleting,
  };
};
